import { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { useOnline } from "../shared/live";
import { navigate, useRoute } from "../shared/router";
import { LoginScreen, Shell, type NavItem } from "../shared/shell";
import { Loading, ToastProvider, useToast } from "../shared/ui";
import { HelpButton } from "./help";
import { registerPwa } from "../shared/install";
import { api, can, canAdmin, Ctx, live, photoQueue, queue, readBuilding, saveBuilding, type Me } from "./ctx";
import { AdminPage } from "./pages/Admin";
import { CrewsPage } from "./pages/Crews";
import { HomePage } from "./pages/Home";
import { OverviewPage } from "./pages/Overview";
import { ProductionPage } from "./pages/Production";
import { GridPage } from "./pages/Grid";
import { HistoryPage } from "./pages/History";
import { IssuesPage } from "./pages/Issues";
import { PhotosPage } from "./pages/Photos";
import { ProjectsPage } from "./pages/Projects";
import { ReportPage } from "./pages/Report";
import { SchedulePage } from "./pages/Schedule";
import { TemplatesPage } from "./pages/Templates";
import { FirstLoginScreen, InviteScreen } from "./pages/Welcome";
import { StateBanner } from "./lifecycle";

type Proj = { id: string; name: string; buildings: string[]; role: string | null; active: boolean; briefs: Record<string, any> };

function nav(me: Me, b: string | null, projects: Proj[]): NavItem[] {
  const proj = projects.find((p) => b && p.buildings.includes(b));
  const boss = !!proj && can(me, "overview");
  const items: NavItem[] = [];
  // главная руководителя — обзор ЖК (дашборд собственника); прораба — его главная на телефоне
  if (boss && proj) items.push({ to: `/p/${proj.id}`, label: "Обзор ЖК", icon: "dashboard", mobile: true });
  if (b && !boss) items.push({ to: `/b/${b}/home`, label: "Главная", icon: "dashboard", mobile: true });
  items.push({ to: "/projects", label: "Проекты", icon: "projects", mobile: !b });
  const reports = can(me, "reports.fill") || can(me, "reports.correct");
  if (b) {
    items.push(
      { to: `/b/${b}`, label: "Производство", icon: "building", mobile: boss, exact: true },
      { to: `/b/${b}/grid`, label: "Шахматка", icon: "grid" },
      { to: `/b/${b}/schedule`, label: "График цикла", icon: "schedule" },
      { to: `/b/${b}/crews`, label: "Бригады", icon: "crew" },
      ...(reports ? [{ to: `/b/${b}/report`, label: "Отчёт прораба", icon: "report", mobile: !boss }] : []),
      { to: `/b/${b}/issues`, label: "Проблемы", icon: "issue", mobile: true },
      { to: `/b/${b}/photos`, label: "Фотоархив", icon: "photo" },
      { to: `/b/${b}/history`, label: "История", icon: "history" },
    );
  }
  if (can(me, "templates.view") || can(me, "templates.edit")) items.push({ to: "/templates", label: "Шаблоны циклов", icon: "template" });
  if (canAdmin(me)) items.push({ to: "/admin", label: "Администрирование", icon: "admin" });
  return items;
}

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [needLogin, setNeedLogin] = useState(!api.token);
  const [building, setBuildingState] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<string[]>([]); // корпуса, доступные пользователю
  const [projects, setProjects] = useState<Proj[]>([]);
  const [queuedReports, setQueuedReports] = useState(0);
  const [queuedPhotos, setQueuedPhotos] = useState(0);
  const queued = queuedReports + queuedPhotos;
  const online = useOnline(live);
  const r = useRoute();
  const toast = useToast();

  const start = useCallback(async () => {
    try {
      const m: Me = await api.get("/me");
      const projects: any[] = await api.get("/projects");
      const ids: string[] = projects.flatMap((p) => p.buildings.map((b: any) => b.id));
      setProjects(projects.map((p) => ({ id: p.id, name: p.name, buildings: p.buildings.map((b: any) => b.id), role: p.role ?? null, active: p.status === "active" || p.buildings.some((b: any) => b.cycles_done > 0), briefs: Object.fromEntries(p.buildings.map((b: any) => [b.id, b])) })));
      // запомненный объект годится, только если он по-прежнему доступен этому пользователю;
      // единственный доступный объект открываем сразу — прорабу не нужен лишний экран выбора
      let chosen = readBuilding(m.id);
      if (chosen && !ids.includes(chosen)) chosen = null;
      if (!chosen && ids.length === 1) chosen = ids[0];
      saveBuilding(m.id, chosen);
      setBuildings(ids);
      setBuildingState(chosen);
      setMe(m);
      live.start();
      setNeedLogin(false);
    } catch {
      setNeedLogin(true);
    }
  }, []);

  useEffect(() => {
    api.onUnauthorized = () => { api.logout(); live.stop(); setMe(null); setNeedLogin(true); };
    if (api.token) start();
    const offReports = queue.subscribe((i) => setQueuedReports(i.length));
    const offPhotos = photoQueue.subscribe((i) => setQueuedPhotos(i.length));
    return () => { offReports(); offPhotos(); };
  }, [start]);

  const setBuilding = useCallback((id: string) => {
    if (me) saveBuilding(me.id, id);
    setBuildingState(id);
  }, [me]);
  const urlBuilding = r.parts[0] === "b" ? r.parts[1] : null;
  const urlAllowed = !urlBuilding || (!!me && can(me, "projects.all")) || buildings.includes(urlBuilding);
  useEffect(() => {
    if (urlBuilding && urlAllowed && urlBuilding !== building) setBuilding(urlBuilding);
  }, [urlBuilding, urlAllowed, building, setBuilding]);
  useEffect(() => {
    // чужой или удалённый объект по ссылке — не показываем ошибки, а предлагаем выбрать свой.
    // Список объектов сначала обновляем: пользователя могли назначить на объект уже после входа.
    if (!me || !urlBuilding || urlAllowed) return;
    let cancelled = false;
    api.get("/projects").then((projects: any[]) => {
      if (cancelled) return;
      const ids: string[] = projects.flatMap((p) => p.buildings.map((x: any) => x.id));
      if (ids.includes(urlBuilding)) { setBuildings(ids); return; }
      toast.error("Этот корпус вам недоступен. Выберите корпус из списка.");
      navigate(building && ids.includes(building) ? `/b/${building}` : "/projects");
    }).catch(() => navigate("/projects"));
    return () => { cancelled = true; };
  }, [me, urlBuilding, urlAllowed, building, toast]);

  if (r.parts[0] === "invite" && r.parts[1]) return <InviteScreen token={r.parts[1]} onDone={start} />;
  if (needLogin) return <LoginScreen api={api} title="Стройка" subtitle="Контроль монолитного строительства по этажам и циклам" onDone={start} />;
  if (!me) return <Loading />;
  if (me.must_change_password) return <FirstLoginScreen me={me} onDone={start} onLogout={() => { api.logout(); live.stop(); setMe(null); setNeedLogin(true); }} />;
  if (urlBuilding && !urlAllowed) return <Loading />;
  if (!r.path || r.path === "/" || (r.parts[0] === "b" && !urlBuilding)) {
    // прораб сразу попадает в отчёт своей смены, руководитель — на дашборд объекта;
    // если объект не выбран и их несколько — на список проектов, где его нужно выбрать
    // руководитель — на «Обзор ЖК» своего проекта; прораб — на главную своего корпуса
    const mine = can(me, "overview") ? projects : [];
    const own = mine.find((x) => x.active) || mine[0];
    if (own) navigate(`/p/${(building && projects.find((x) => x.buildings.includes(building))?.id) || own.id}`);
    else if (!building) navigate("/projects");
    else navigate(`/b/${building}/home`);
    return null;
  }
  const logout = () => { api.logout(); live.stop(); setMe(null); setNeedLogin(true); location.hash = "/"; };
  // на «Обзоре ЖК» корпус для меню — выбранный в этом ЖК или первый его корпус
  const viewProject = r.parts[0] === "p" ? projects.find((x) => x.id === r.parts[1]) : undefined;
  const b = urlBuilding || (viewProject && !(building && viewProject.buildings.includes(building)) ? viewProject.buildings[0] || null : building);
  const page = (() => {
    if (r.parts[0] === "projects") return <ProjectsPage />;
    // разделы, не положенные роли, не открываются и по прямому адресу (сервер проверяет права отдельно)
    if (r.parts[0] === "templates") return can(me, "templates.view") || can(me, "templates.edit") ? <TemplatesPage id={r.parts[1]} /> : <ProjectsPage />;
    if (r.parts[0] === "admin") return canAdmin(me) ? <AdminPage /> : <ProjectsPage />;
    if (r.parts[0] === "p" && r.parts[1]) {
      const pr = projects.find((x) => x.id === r.parts[1]);
      return pr && can(me, "overview")
        ? <OverviewPage projectId={pr.id} projects={projects} />
        : <ProjectsPage />;
    }
    if (r.parts[0] === "b" && b) {
      switch (r.parts[2]) {
        case "grid": return <GridPage id={b} />;
        case "schedule": return <SchedulePage id={b} />;
        case "crews": return <CrewsPage id={b} />;
        case "report": return <ReportPage id={b} />;
        case "issues": return <IssuesPage id={b} />;
        case "history": return <HistoryPage id={b} />;
        case "photos": return <PhotosPage id={b} />;
        case "home": return <HomePage id={b} />;
        default: return <ProductionPage id={b} />;
      }
    }
    return <ProjectsPage />;
  })();
  return (
    <Ctx.Provider value={{ me, buildingId: b, setBuilding, buildings }}>
      <Shell brand="Стройка" nav={nav(me, b, projects)} user={me.full_name} onLogout={logout} online={online} queued={queued}>
        {r.parts[0] === "b" && b && <StateBanner b={projects.find((x) => x.buildings.includes(b))?.briefs[b]} />}
        {page}
      </Shell>
      <HelpButton />
    </Ctx.Provider>
  );
}

registerPwa();

createRoot(document.getElementById("root")!).render(
  <ToastProvider>
    <App />
  </ToastProvider>,
);
